const chapter = {
  id: 'environmental-chemistry',
  slug: 'environmental-chemistry',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE / NEET',
  title: {
    english: 'Environmental Chemistry',
        hindi: 'पर्यावरण रसायन',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question in NEET; rare in JEE',
        hindi: 'नीट में 1 प्रश्न; जेईई में दुर्लभ',
  },
  syllabusTags: ['Environmental', 'Class-11', 'NEET'],
  sections: [
    {
      id: 'atmosphere',
      title: {
        english: 'Atmospheric Layers & Pollution',
        hindi: 'वायुमण्डलीय परतें और प्रदूषण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Troposphere** (0-12 km) — weather, $N_2, O_2, CO_2, H_2O$.',
        hindi: '**क्षोभमंडल** (0-12 किमी) — मौसम, $N_2, O_2, CO_2, H_2O$।',
            },
            {
              english: '**Stratosphere** (12-50 km) — ozone layer; absorbs UV.',
        hindi: '**समताप मंडल** (12-50 किमी) - ओजोन परत; यूवी को अवशोषित करता है।',
            },
            {
              english: '**Mesosphere** (50-85 km).',
        hindi: '**मेसोस्फीयर** (50-85 किमी)।',
            },
            {
              english: '**Thermosphere/Ionosphere** (85+ km).',
        hindi: '**तापमंडल/आयनमंडल** (85+ किमी)।',
            },
            {
              english: '**Pollutant types:** primary (released directly: $CO, NO_x, SO_x, $ HCs, particulates) vs secondary (formed in air: $O_3, PAN, H_2SO_4, $ aldehydes).',
        hindi: '**प्रदूषक प्रकार:** प्राथमिक (सीधे जारी: $CO, NO_x, SO_x, $ HCs, कण) बनाम द्वितीयक (हवा में निर्मित: $O_3, PAN, H_2SO_4, $ एल्डिहाइड)।',
            },
          ],
        },
      ],
    },
    {
      id: 'air-pollution',
      title: {
        english: 'Air Pollution',
        hindi: 'वायु प्रदूषण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**$CO$** — colourless, odourless; binds Hb 300× more strongly than $O_2$ → carboxyhaemoglobin → asphyxiation.',
        hindi: '**$CO$** - रंगहीन, गंधहीन; Hb 300× को $O_2$ → कार्बोक्सीहीमोग्लोबिन → श्वासावरोध से अधिक मजबूती से बांधता है।',
            },
            {
              english: '**$CO_2$** — major **greenhouse gas**; from combustion of fossil fuels & deforestation. Atmospheric level rising — global warming.',
        hindi: '**$CO_2$** — प्रमुख **ग्रीनहाउस गैस**; जीवाश्म ईंधन के दहन और वनों की कटाई से। वायुमंडलीय स्तर में वृद्धि - ग्लोबल वार्मिंग।',
            },
            {
              english: '**$SO_2$** — coal-burning, smelting; oxidised to $SO_3$ → $H_2SO_4$ → **acid rain** (pH < 5.6). Damages stone (marble cancer), corrodes metals, harms vegetation.',
        hindi: '**$SO_2$** - कोयला जलाना, गलाना; $SO_3$ → $H_2SO_4$ → **अम्लीय वर्षा** (पीएच <5.6) में ऑक्सीकृत। पत्थर को नुकसान पहुंचाता है (संगमरमर का कैंसर), धातुओं को नष्ट करता है, वनस्पति को नुकसान पहुंचाता है।',
            },
            {
              english: '**$NO_x$** ($NO, NO_2$) — vehicles, lightning; with HCs in sunlight → **photochemical smog** (LA-type) containing $O_3$, PAN (peroxyacetyl nitrate), aldehydes, $NO_2$. Eye irritation, plant damage.',
        hindi: '**$NO_x$** ($NO, NO_2$) - वाहन, बिजली; सूरज की रोशनी में HCs के साथ → **फोटोकेमिकल स्मॉग** (LA-प्रकार) जिसमें $O_3$, PAN (पेरोक्सीएसिटाइल नाइट्रेट), एल्डिहाइड, $NO_2$ होता है। आंखों में जलन, पौधों को नुकसान.',
            },
            {
              english: '**Classical (London) smog** — cool, humid; mixture of smoke + $SO_2$ + fog; reducing.',
        hindi: '**क्लासिकल (लंदन) स्मॉग** - ठंडा, आर्द्र; धुएँ का मिश्रण + $SO_2$ + कोहरा; कम करना.',
            },
            {
              english: '**Particulate matter** — PM2.5, PM10; cause respiratory disease.',
        hindi: '**पार्टिकुलेट मैटर** — PM2.5, PM10; श्वसन रोग का कारण बनता है.',
            },
            {
              english: '**Greenhouse gases** in order of warming potential per molecule: $CFCs > CH_4 > N_2O > O_3 > CO_2 > H_2O$. By total contribution: $CO_2$ dominates.',
        hindi: '**ग्रीनहाउस गैसें** प्रति अणु तापन क्षमता के क्रम में: $CFCs > CH_4 > N_2O > O_3 > CO_2 > H_2O$। कुल योगदान से: $CO_2$ हावी है।',
            },
          ],
        },
        {
          type: 'note',
          body: {
            english: '**Acid rain composition:** ~60% $H_2SO_4$, 30% $HNO_3$, 10% HCl, plus carbonic acid.',
        hindi: '**अम्लीय वर्षा संरचना:** ~60% $H_2SO_4$, 30% $HNO_3$, 10% HCl, प्लस कार्बोनिक एसिड।',
          },
        },
      ],
    },
    {
      id: 'ozone-depletion',
      title: {
        english: 'Stratospheric Ozone Depletion',
        hindi: 'समताप मण्डलीय ओज़ोन अवक्षय',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: 'Ozone in stratosphere shields Earth from UV-B (280-315 nm). Formed by $O_2 + h\\nu \\to 2O$; then $O + O_2 \\to O_3$. Equilibrium maintained until disturbed by Cl free radicals from CFCs.',
        hindi: 'समताप मंडल में ओजोन पृथ्वी को यूवी-बी (280-315 एनएम) से बचाता है। $O_2 + h\\nu \\to 2O$ द्वारा निर्मित; फिर $O + O_2 \\to O_3$. सीएफसी से सीएल मुक्त कणों द्वारा परेशान होने तक संतुलन बनाए रखा जाता है।',
          },
        },
        {
          type: 'equation',
          equation: 'CF_2Cl_2 \\xrightarrow{UV} \\dot{Cl} + \\dot{C}F_2Cl',
        },
        {
          type: 'equation',
          equation: '\\dot{Cl} + O_3 \\to ClO\\cdot + O_2; \\quad ClO\\cdot + O \\to \\dot{Cl} + O_2',
        },
        {
          type: 'list',
          items: [
            {
              english: 'One Cl radical can destroy up to 100,000 ozone molecules (catalytic chain).',
        hindi: 'एक सीएल रेडिकल 100,000 ओजोन अणुओं (उत्प्रेरक श्रृंखला) को नष्ट कर सकता है।',
            },
            {
              english: '**Antarctic ozone hole** (Sept-Oct) — formed on polar stratospheric clouds; $ClO\\cdot$ dimers release Cl in spring sun.',
        hindi: '**अंटार्कटिक ओजोन छिद्र** (सितंबर-अक्टूबर) - ध्रुवीय समतापमंडलीय बादलों पर बनता है; $ClO\\cdot$ डिमर वसंत की धूप में सीएल छोड़ते हैं।',
            },
            {
              english: 'Effects of ozone depletion: skin cancer, cataracts, immune suppression, crop damage, plankton harm.',
        hindi: 'ओजोन रिक्तीकरण के प्रभाव: त्वचा कैंसर, मोतियाबिंद, प्रतिरक्षा दमन, फसल क्षति, प्लवक क्षति।',
            },
            {
              english: '**Montreal Protocol** (1987) phased out CFCs; replaced by HCFCs, then HFCs (no Cl).',
        hindi: '**मॉन्ट्रियल प्रोटोकॉल** (1987) ने सीएफसी को चरणबद्ध तरीके से समाप्त कर दिया; एचसीएफसी द्वारा प्रतिस्थापित, फिर एचएफसी (कोई सीएल नहीं)।',
            },
          ],
        },
      ],
    },
    {
      id: 'water-soil',
      title: {
        english: 'Water & Soil Pollution',
        hindi: 'जल और मृदा प्रदूषण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Water pollutants:** pathogens, organic waste, plant nutrients ($NO_3^-, PO_4^{3-}$), industrial chemicals (heavy metals: Pb, Hg, Cd, As), thermal pollutants.',
        hindi: '**जल प्रदूषक:** रोगजनक, जैविक अपशिष्ट, पौधों के पोषक तत्व ($NO_3^-, PO_4^{3-}$), औद्योगिक रसायन (भारी धातु: Pb, Hg, Cd, As), तापीय प्रदूषक।',
            },
            {
              english: '**BOD (biochemical oxygen demand):** mg of $O_2$ used by microbes to oxidise organic matter in 1 L of water at 20 °C in 5 days. Clean water < 5 ppm; polluted > 17 ppm.',
        hindi: '**बीओडी (जैव रासायनिक ऑक्सीजन मांग):** $O_2$ का मिलीग्राम 5 दिनों में 20 डिग्री सेल्सियस पर 1 लीटर पानी में कार्बनिक पदार्थ को ऑक्सीकरण करने के लिए सूक्ष्मजीवों द्वारा उपयोग किया जाता है। साफ़ पानी <5 पीपीएम; प्रदूषित> 17 पीपीएम।',
            },
            {
              english: '**COD (chemical oxygen demand):** $O_2$ needed using strong oxidant ($K_2Cr_2O_7$); always > BOD.',
        hindi: '**COD (रासायनिक ऑक्सीजन मांग):** $O_2$ को मजबूत ऑक्सीडेंट ($K_2Cr_2O_7$) का उपयोग करने की आवश्यकता है; सदैव > बीओडी.',
            },
            {
              english: '**Eutrophication:** nutrient enrichment ($N, P$) → algal bloom → $O_2$ depletion → fish kill.',
        hindi: '**यूट्रोफिकेशन:** पोषक तत्व संवर्धन ($N, P$) → शैवाल का खिलना → $O_2$ कमी → मछली का मरना।',
            },
            {
              english: '**Heavy metal toxicity:** Hg → Minamata disease; Pb → neurological damage; Cd → itai-itai; As → black-foot.',
        hindi: '**भारी धातु विषाक्तता:** एचजी → मिनामाटा रोग; पंजाब → तंत्रिका संबंधी क्षति; सीडी → इटाई-इटाई; जैसे → काला-पैर।',
            },
            {
              english: '**International standards (drinking water):** F⁻ < 1 ppm, Pb < 50 ppb, As < 50 ppb (BIS 10 ppb).',
        hindi: '**अंतर्राष्ट्रीय मानक (पीने का पानी):** एफ⁻ <1 पीपीएम, पीबी <50 पीपीबी, एएस <50 पीपीबी (बीआईएस 10 पीपीबी)।',
            },
            {
              english: '**Soil pollutants:** pesticides (DDT — non-biodegradable, biomagnification), herbicides, fertilisers in excess.',
        hindi: '**मिट्टी प्रदूषक:** कीटनाशक (डीडीटी - गैर-बायोडिग्रेडेबल, बायोमैग्नीफिकेशन), शाकनाशी, उर्वरक अधिक मात्रा में।',
            },
          ],
        },
      ],
    },
    {
      id: 'green-chemistry',
      title: {
        english: 'Green Chemistry',
        hindi: 'हरित रसायन',
      },
      blocks: [
        {
          type: 'definition',
          title: {
            english: 'Green Chemistry',
        hindi: 'हरित रसायन',
          },
          body: {
            english: 'Designing chemical processes & products to reduce or eliminate use & generation of hazardous substances.',
        hindi: 'खतरनाक पदार्थों के उपयोग और उत्पादन को कम करने या समाप्त करने के लिए रासायनिक प्रक्रियाओं और उत्पादों को डिजाइन करना।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Use of $H_2O_2$ instead of $Cl_2$ in paper bleaching.',
        hindi: 'पेपर ब्लीचिंग में $Cl_2$ के बजाय $H_2O_2$ का उपयोग।',
            },
            {
              english: 'Use of safer solvents (ionic liquids, supercritical $CO_2$).',
        hindi: 'सुरक्षित सॉल्वैंट्स (आयनिक तरल पदार्थ, सुपरक्रिटिकल $CO_2$) का उपयोग।',
            },
            {
              english: 'Catalytic processes (atom-economical) over stoichiometric reagents.',
        hindi: 'स्टोइकोमेट्रिक अभिकर्मकों पर उत्प्रेरक प्रक्रियाएं (परमाणु-आर्थिक)।',
            },
            {
              english: 'Synthesis of ibuprofen by BHC method — fewer steps, higher atom economy.',
        hindi: 'बीएचसी विधि द्वारा इबुप्रोफेन का संश्लेषण - कम कदम, उच्च परमाणु अर्थव्यवस्था।',
            },
            {
              english: 'Dry cleaning using liquid $CO_2$ (replaces tetrachloroethene).',
        hindi: 'तरल $CO_2$ (टेट्राक्लोरोएथीन की जगह) का उपयोग करके ड्राई क्लीनिंग।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Acid rain pH} < 5.6',
      desc: {
        english: '',
        hindi: 'अम्लीय वर्षा pH <5.6',
      },
    },
    {
      latex: 'CO + Hb \\to \\text{COHb (300}\\times\\text{ affinity)}',
      desc: {
        english: '',
        hindi: 'सीओ + एचबी सीओएचबी (300 एफ़िनिटी)',
      },
    },
    {
      latex: 'CFC \\xrightarrow{UV} Cl\\cdot \\xrightarrow{O_3} ClO\\cdot + O_2',
      desc: {
        english: 'Ozone depletion',
        hindi: 'ओजोन रिक्तीकरण',
      },
    },
    {
      latex: '\\text{BOD} < 5 \\text{ ppm (clean)}',
      desc: {
        english: '',
        hindi: 'बीओडी <5 पीपीएम (स्वच्छ)',
      },
    },
    {
      latex: '\\text{COD} > \\text{BOD always}',
      desc: {
        english: '',
        hindi: 'सीओडी > बीओडी हमेशा',
      },
    },
    {
      latex: '\\text{F}^- < 1 \\text{ ppm in drinking water}',
      desc: {
        english: '',
        hindi: 'पीने के पानी में एफ - <1 पीपीएम',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Pollution',
      problem: {
        english: 'Photochemical smog contains:',
        hindi: 'फोटोकैमिकल स्मॉग में शामिल हैं:',
      },
      answer: {
        english: 'O₃, PAN, NO₂, aldehydes',
        hindi: 'O₃, PAN, NO₂, एल्डिहाइड',
      },
      solution: {
        english: 'Formed from NO_x + HCs in sunlight.',
        hindi: 'सूर्य के प्रकाश में NO_x + HCs से बनता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Ozone',
      problem: {
        english: 'CFCs deplete ozone via:',
        hindi: 'सीएफसी निम्न के माध्यम से ओजोन को नष्ट करते हैं:',
      },
      answer: {
        english: 'Free radical chain — Cl· catalyses O₃ → O₂',
        hindi: 'मुक्त मूलक श्रृंखला - सीएल· ओ₃ → ओ₂ उत्प्रेरित करता है',
      },
      solution: {
        english: '',
        hindi: 'सीएफसी से क्लोरीन मुक्त मूलक (Cl·) बनते हैं, जो ओजोन को श्रृंखलाबद्ध अभिक्रियाओं से $O_2$ में बदलते रहते हैं; इसलिए ओजोन क्षय होता है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'BOD',
      problem: {
        english: 'Higher BOD indicates:',
        hindi: 'उच्च बीओडी इंगित करता है:',
      },
      answer: {
        english: 'More organic pollution (more O₂ used by microbes)',
        hindi: 'अधिक जैविक प्रदूषण (रोगाणुओं द्वारा उपयोग किया जाने वाला अधिक O₂)',
      },
      solution: {
        english: '',
        hindi: 'उच्च BOD का अर्थ है कि सूक्ष्मजीव अधिक घुलित ऑक्सीजन खर्च कर रहे हैं, यानी जल में कार्बनिक प्रदूषण अधिक है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Toxicity',
      problem: {
        english: 'Minamata disease is caused by:',
        hindi: 'मिनामाटा रोग किसके कारण होता है:',
      },
      answer: {
        english: 'Mercury poisoning (methylmercury in fish)',
        hindi: 'पारा विषाक्तता (मछली में मिथाइलमेरकरी)',
      },
      solution: {
        english: 'Bioaccumulation up the food chain.',
        hindi: 'खाद्य श्रृंखला में जैवसंचय।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Greenhouse',
      problem: {
        english: 'Major contributor to global warming:',
        hindi: 'ग्लोबल वार्मिंग में प्रमुख योगदानकर्ता:',
      },
      answer: {
        english: 'CO₂',
        hindi: 'CO₂',
      },
      solution: {
        english: 'Highest absolute concentration even though CFCs more potent per molecule.',
        hindi: 'उच्चतम निरपेक्ष सांद्रता, भले ही सीएफसी प्रति अणु अधिक शक्तिशाली हो।',
      },
    },
  ],
  next: null,
};

export default chapter;
